import type { StatusTone } from '@neo/primitives/status-indicator'
import { isEmbedMode } from 'v2/shared/utils/embed-session'
import type { RIndiaKycBusiness } from 'v2/types/api/india-kyc'
import { getLocalAuth } from 'v2/utils'
import dayjs from 'v2/utils/dayjs'

/** The timeline's last step: what the signatory has done so far, or why the attempt ended. */
export interface BusinessKycOutcome {
  title: string
  badge: { label: string; tone: StatusTone }
  detail: string
}

/** The account owner's view of its first business KYC, which the authorised signatory completes. */
interface BusinessKycStatus {
  /** Null with no attempt, or after the owner cancelled one: nothing to say. */
  outcome: BusinessKycOutcome | null
  /** Still running, so a new one cannot start (core answers business_in_progress). */
  isOpen: boolean
  /** Resend and Cancel: core allows both in every in-progress step but finalizing. */
  canChange: boolean
  /** Ended without a CAF for a reason the owner did not choose, so they restart it. */
  isStopped: boolean
}

const NONE: BusinessKycStatus = { outcome: null, isOpen: false, canChange: false, isStopped: false }

const firstName = (name: string) => name.trim().split(/\s+/)[0] ?? name

/** What the signatory is asked to do, which the eSign switch decides. */
export const signatoryTask = (esignRequired: boolean) =>
  esignRequired ? 'eSign the application with Aadhaar' : 'submit the application'

/** What the owner does about a stopped business KYC. */
export const businessKycRestartHint = ({ status }: RIndiaKycBusiness): string =>
  status === 'failed'
    ? 'Restart KYC to send your signatory a new link.'
    : 'Restart KYC to verify with a different signatory.'

export const hasLinkExpired = ({ link_expires_at }: RIndiaKycBusiness) =>
  dayjs(link_expires_at).isBefore(dayjs())

// Every outcome but a stopped one is the signed-and-verified step, done or still waiting.
const signed = (badge: BusinessKycOutcome['badge'], detail: string): BusinessKycOutcome => ({
  title: 'Signed and verified',
  badge,
  detail,
})
const REJECTED = { label: 'Rejected', tone: 'danger' } as const

// The owner hears that a document was rejected; why, and the fix, are on the signatory's screens.
const progressOutcome = (business: RIndiaKycBusiness): BusinessKycOutcome => {
  const { signatory, step, code, esign_required } = business
  const first = firstName(signatory.name)
  const waiting = { label: `Waiting for ${first}`, tone: 'warning' } as const
  const task = signatoryTask(esign_required)
  // kyc_unavailable here is core losing the stored file, not a refusal.
  if (step === 'document' && code === 'kyc_unavailable') {
    return signed(waiting, `${signatory.name} needs to upload the business document again.`)
  }
  if (step === 'document' && code) {
    return signed(waiting, `${first}'s document wasn't accepted. They need to upload another one.`)
  }
  if (step === 'checking') {
    return signed(
      { label: 'Checking', tone: 'progress' },
      `We're checking ${first}'s business document.`,
    )
  }
  if (step === 'signing') {
    // Expiry closes only invited and document, so a signing attempt waits for the owner's resend.
    return hasLinkExpired(business)
      ? signed(
          { label: 'Link expired', tone: 'danger' },
          `Resend the link so ${first} can ${task}.`,
        )
      : signed(waiting, `Document accepted. ${first} needs to ${task}.`)
  }
  if (step === 'finalizing') {
    return signed(
      { label: 'Setting up', tone: 'progress' },
      `${signatory.name} ${esign_required ? 'signed' : 'submitted'} the application. We're setting up your business KYC.`,
    )
  }
  return signed(waiting, `${first} needs to upload the business document and ${task}.`)
}

const stoppedOutcome = ({
  status,
  code,
  cancel_reason,
  signatory,
}: RIndiaKycBusiness): BusinessKycOutcome => {
  // The signatory finished; core turned the document down. Only a name mismatch has its own fix.
  if (status === 'failed') {
    const detail =
      code === 'esign_name_mismatch'
        ? `The name on the Aadhaar eSign didn't match ${signatory.name}. Restart KYC with the signatory's name exactly as on their Aadhaar.`
        : 'The business document they uploaded could not be verified.'
    return { title: 'Signatory verification', badge: REJECTED, detail }
  }
  const declined = `Declined by ${firstName(signatory.name)}`
  if (cancel_reason === 'not_signatory') {
    // In embed or hosted mode the local auth is any console login in this browser, not this account.
    const organization = isEmbedMode() ? undefined : getLocalAuth().organization_name
    return {
      title: declined,
      badge: REJECTED,
      detail: `${signatory.name} declined to sign for ${organization || 'your business'}. Restart KYC with a different signatory.`,
    }
  }
  if (cancel_reason === 'no_documents') {
    return {
      title: declined,
      badge: REJECTED,
      detail: `${signatory.name} does not have a GST certificate, Certificate of Incorporation or Udyam certificate. Restart KYC once one is available, or verify as an individual.`,
    }
  }
  if (cancel_reason === 'expired') {
    return {
      title: 'Link expired',
      badge: { label: 'Expired', tone: 'danger' },
      detail: `The link expired after 7 days, before ${firstName(signatory.name)} finished.`,
    }
  }
  return {
    title: 'Cancelled',
    badge: { label: 'Cancelled', tone: 'neutral' },
    detail: 'This business KYC was cancelled.',
  }
}

// core's review codes; any other code gets the generic line.
const REVIEW_REASONS: Record<string, string> = {
  checks_error: "We couldn't finish checking the document automatically.",
  gst_state_mismatch: "The state in the GSTIN doesn't match the address in the GST record.",
  caf_render_failed: "We couldn't fill in the application form from the government record.",
  esign_unverified: "We couldn't confirm the Aadhaar eSign automatically.",
}

/** Why core holds a business KYC for a person, for the owner and the signatory alike. */
export const businessReviewReason = (code: string | null): string | undefined =>
  code ? REVIEW_REASONS[code] : undefined

const reviewOutcome = (code: string | null): BusinessKycOutcome => {
  const reason = businessReviewReason(code)
  return signed(
    { label: 'In review', tone: 'warning' },
    reason
      ? `${reason} Our team is checking it. The result will show here.`
      : 'Our team is checking a few details. The result will show here.',
  )
}

const approvedOutcome = ({ signatory, esign_required }: RIndiaKycBusiness) =>
  signed(
    { label: 'Verified', tone: 'success' },
    `${esign_required ? 'eSigned with Aadhaar' : 'Submitted'} by ${signatory.name}. Business limits now apply.`,
  )

export const describeBusinessKyc = (
  business: RIndiaKycBusiness | null | undefined,
): BusinessKycStatus => {
  if (!business) return NONE
  if (business.status === 'in_progress') {
    const canChange = business.step !== 'finalizing'
    return { ...NONE, outcome: progressOutcome(business), isOpen: true, canChange }
  }
  if (business.status === 'review') {
    return { ...NONE, outcome: reviewOutcome(business.code), isOpen: true }
  }
  // Approved means a business CAF, which today's business screens show; an open timeline ends on it.
  if (business.status === 'approved') return { ...NONE, outcome: approvedOutcome(business) }
  const isStopped =
    business.status === 'failed' ||
    (business.status === 'cancelled' && business.cancel_reason !== 'owner_cancelled')
  return isStopped ? { ...NONE, outcome: stoppedOutcome(business), isStopped } : NONE
}

const ERROR_COPY: Record<string, string> = {
  invalid_signatory:
    'Check the details: name and designation in English, up to 100 characters each, a valid email and an Indian mobile number.',
  // eSign off only: the declaration line the CAF prints holds both, with the date.
  signatory_too_long:
    'The name and designation are too long to print together on the application form. Shorten the designation.',
  confirm_required: 'Confirm this person is an authorised signatory of the business.',
  // The hosted invite's legal business name.
  invalid_business_name: 'Enter your legal business name, up to 99 characters.',
  unsafe_characters: 'Remove < and > from the name and designation.',
  business_in_progress: 'A business KYC is already in progress. Resend or cancel it instead.',
  kyc_exists: 'Your business KYC is already done.',
  step_out_of_order:
    'The business KYC has moved on since this page loaded. Check its latest status.',
}

/** Copy for a refused owner request; an outage (kyc_unavailable) or no code gets the action's own. */
export const businessKycErrorCopy = (code: string | undefined, unavailableCopy: string): string =>
  (code && ERROR_COPY[code]) || unavailableCopy
